import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { YedekParcaData } from '../types/ServiceRequest';
import { useUser } from '../contexts/UserContext';
import ProductImage from './ProductImage';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import AnimatedCloseButton from './AnimatedCloseButton';

interface SparePartInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  sparePart: YedekParcaData;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onSave: () => void;
}

const SparePartInfoModal: React.FC<SparePartInfoModalProps> = ({
  isOpen,
  onClose,
  sparePart,
  quantity,
  onQuantityChange,
  onSave
}) => {
  const { userData } = useUser();
  
  // Scroll engelleyici hook kullan
  useScrollPrevention(isOpen);

  // Modal geri düğmesi handler
  const handleModalClose = useCallback(() => {
    // State'leri temizle
    onQuantityChange(1); // Miktarı varsayılan değere döndür
    onClose();
  }, [onClose, onQuantityChange]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: true
  });

  // Stok durumunu kullanıcı ayarlarına göre göster
  const renderStockInfo = () => {
    // ShowStock false ise sadece var/yok göster
    if (userData && !userData.ShowStock) {
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
          sparePart.Stok > 0
            ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700'
            : 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700'
        }`}>
          {sparePart.Stok > 0 ? 'Var' : 'Yok'}
        </span>
      );
    }
    
    // ShowStock true ise stok miktarını göster
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
        sparePart.CariStokDurumu
          ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700'
          : 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700'
      }`}>
        {sparePart.CariStokDurumu ? `${sparePart.Stok} adet mevcut` : 'Stok yok'}
      </span>
    );
  };





  const handleClose = () => {
    handleModalClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleSave = () => {
    onSave();
    handleClose();
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto bg-black/20 dark:bg-black/40 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
            {/* Modal */}
            <div className="flex min-h-full items-center justify-center px-2 py-4 text-center sm:p-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative transform transition-transform overflow-hidden rounded-xl backdrop-blur-xs bg-gradient-to-br from-white/90 via-white/88 to-white/85 dark:from-gray-700/85 dark:via-gray-750/83 dark:to-gray-800/85 border border-white/50 dark:border-gray-600/50 text-left shadow-2xl shadow-black/10 dark:shadow-black/30 w-full mx-2 sm:mx-0 sm:my-8 sm:w-full sm:max-w-lg"
              >
              {/* Header */}
              <div className="border-b border-gray-300 dark:border-gray-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Yedek Parça Bilgileri
                  </h3>
                  <AnimatedCloseButton 
                    onClick={handleClose}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <div className="flex items-start space-x-4">
                  {/* Parça Görseli */}
                  <div className="flex-shrink-0">
                    <ProductImage
                      imageId={sparePart.Gorsel}
                      alt={sparePart.Ad}
                      size="w-24 h-24"
                      iconSize="w-12 h-12"
                      containerClassName="p-2"
                    />
                  </div>

                  {/* Parça Bilgileri */}
                  <div className="flex-1 min-w-0">
                    <div className="mb-3">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white break-words">
                        {sparePart.Ad}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Kod: <span className="font-mono text-xs">{sparePart.Kod}</span>
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Grup: {sparePart.GrupAdi}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Liste Fiyatı:</span>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          ₺{sparePart.ListeFiyati?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Bayi Fiyatı:</span>
                        <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                          ₺{sparePart.BayiFiyati?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>

                    {/* Stok Durumu */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Stok:</span>
                        {renderStockInfo()}
                      </div>
                    </div>

                    {/* İşçilik Bedeli */}
                    {sparePart.IscilikBedeli > 0 && (
                      <div className="mb-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">İşçilik Bedeli:</span>
                        <p className="text-base font-medium text-gray-900 dark:text-white">
                          ₺{sparePart.IscilikBedeli?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Miktar */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Miktar
                  </label>
                  <div className="flex items-center gap-2 max-w-xs">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={quantity}
                      onChange={(e) => onQuantityChange(parseInt(e.target.value) || 1)}
                      className="form-input w-24 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500"
                    />

                    <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                      <button
                        type="button"
                        onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        className="h-11 rounded-l-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <div className="rounded-l-lg border border-white dark:border-gray-700 px-3 h-full flex items-center justify-center text-gray-600 dark:text-gray-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 12H6" />
                          </svg>
                        </div>
                      </button>
                      
                      <div className="w-px h-11 bg-gray-300 dark:bg-gray-600"></div>
                      
                      <button
                        type="button"
                        onClick={() => onQuantityChange(Math.min(100, quantity + 1))}
                        disabled={quantity >= 100}
                        className="h-11 rounded-r-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <div className="rounded-r-lg border border-white dark:border-gray-700 px-3 h-full flex items-center justify-center text-gray-600 dark:text-gray-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-300 dark:border-gray-600 px-6 py-4">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                  >
                    <div className="rounded-lg border border-white dark:border-gray-700 px-5 py-2 text-base font-medium text-gray-700 dark:text-gray-300">
                      İptal
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-5 py-2 text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg shadow-lg shadow-blue-500/25 transform transition-all duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SparePartInfoModal;
